import * as THREE from 'three';
import type { EnemyAnimationRole } from '../../shared/animation/animationRoles';
import type { EnemyAnimationProfileDefinition } from '../../shared/animation/animationProfileTypes';
import type { LoadedModelInstance } from './animatedModelInstanceFactory';
import { createAnimationClipResolver, type AnimationClipResolver } from './animationClipResolver';
import { createAnimationInstance, type EnemyAnimationInstance } from './enemyAnimationInstance';
import {
  isAttackRole,
  resolveEnemyAnimationState,
  type EnemyAnimationPresentationState,
} from './enemyAnimationStateResolver';
import { animationTelemetry } from './animationTelemetry';
import { disposeAnimationInstance } from './animationCleanup';
import { actionCueElapsedFraction } from '../../shared/animation/enemyActionCue';

export interface EnemyAnimationContinuity {
  role: EnemyAnimationRole | null;
  normalizedTime: number;
  dead: boolean;
  lastCueSequence: number;
}

/** Deterministic per-enemy phase seed (id + match seed). */
export function animationPhaseSeed(enemyId: number, matchSeed = 0): number {
  let h = (Math.imul(enemyId + 1, 2654435761) ^ Math.imul(matchSeed + 1, 2246822519)) >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 2246822507) >>> 0;
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

/**
 * Owns one animated presentation instance: builds actions, selects semantic
 * roles from authoritative state, cross-fades, scales locomotion playback,
 * locks death, and cleans up. It never mutates gameplay.
 */
export class EnemyAnimationController {
  readonly instance: EnemyAnimationInstance;
  private readonly resolver: AnimationClipResolver;
  private readonly stateMap: Partial<Record<EnemyAnimationRole, { loop: 'repeat' | 'once' | 'pingPong'; clampWhenFinished?: boolean; timeScale?: number; interruptPriority?: number }>>;
  private lastCueSequence = 0;

  constructor(
    readonly profile: EnemyAnimationProfileDefinition,
    readonly model: LoadedModelInstance,
    phaseSeed: number,
    resolver: AnimationClipResolver = createAnimationClipResolver(),
  ) {
    this.resolver = resolver;
    this.stateMap = profile.playback ?? {};
    this.instance = createAnimationInstance(model.root, phaseSeed);
    this.buildActions();
    animationTelemetry.liveMixers++;
  }

  static create(
    profile: EnemyAnimationProfileDefinition,
    model: LoadedModelInstance,
    phaseSeed: number,
  ): EnemyAnimationController {
    return new EnemyAnimationController(profile, model, phaseSeed);
  }

  /** Build a paused action for every role that resolves to a real clip. */
  private buildActions(): void {
    for (const clip of this.model.source.animations) {
      const action = this.instance.mixer.clipAction(clip);
      action.paused = true;
      action.enabled = false;
    }
    for (const role of Object.keys(this.profile.clips) as EnemyAnimationRole[]) {
      const resolved = this.resolver.resolve(this.profile, role, this.model.source.animations);
      if (!resolved) continue;
      const action = this.instance.mixer.clipAction(resolved.clip);
      if (!action) continue;
      this.instance.actions.set(role, action);
      this.applyPlayback(role, action);
    }
    animationTelemetry.animationActionCount += this.instance.actions.size;
  }

  private applyPlayback(role: EnemyAnimationRole, action: THREE.AnimationAction): void {
    const settings = this.stateMap[role];
    if (settings) {
      if (settings.loop === 'once') {
        action.setLoop(THREE.LoopOnce, 1);
        if (settings.clampWhenFinished !== false) action.clampWhenFinished = true;
      } else if (settings.loop === 'pingPong') {
        action.setLoop(THREE.LoopPingPong, Infinity);
      } else {
        action.setLoop(THREE.LoopRepeat, Infinity);
      }
      if (settings.timeScale) action.timeScale = settings.timeScale;
    } else {
      action.setLoop(THREE.LoopRepeat, Infinity);
    }
  }

  /** Update presentation from authoritative state (called on the render frame). */
  update(state: EnemyAnimationPresentationState, dt: number): void {
    this.syncState(state);
    this.advance(dt);
  }

  /** Resolve semantic state without advancing the skeleton. */
  syncState(state: EnemyAnimationPresentationState): void {
    const anim = this.instance;
    if (anim.dead) {
      return;
    }
    const resolution = resolveEnemyAnimationState(this.profile, state);
    if (!resolution.role) {
      return;
    }

    if (resolution.role === 'death') {
      this.playRole(resolution.role, { force: true, duration: this.profile.transitions.deathCrossFadeSeconds });
      anim.dead = true;
      return;
    }

    const current = anim.currentRole;
    if (current && current === resolution.role && anim.currentAction?.isRunning()) {
      this.applyLocomotion(resolution.role, state.speed);
      return;
    }

    // A completed one-shot releases its interrupt priority so the resolved
    // baseline state (normally idle/locomotion) can take control again.
    if (anim.currentAction && this.isFinishedOneShot(anim.currentAction)) {
      anim.currentPriority = 0;
    }

    const isNewCue = state.cue ? state.cue.sequence > this.lastCueSequence : false;
    this.playRole(resolution.role, {
      duration: this.transitionDuration(current, resolution.role),
    });
    // Late cues align to authoritative elapsed time so a reconnecting client
    // reconstructs an in-progress attack/transition at the right phase.
    if (isNewCue && state.cue) {
      this.lastCueSequence = state.cue.sequence;
      const action = this.instance.currentAction;
      if (action && state.cue.durationTicks > 0) {
        const elapsed = actionCueElapsedFraction(state.cue, state.currentTick ?? 0);
        action.time = elapsed * action.getClip().duration;
      }
    }
    this.applyLocomotion(resolution.role, state.speed);
  }

  /** Advance the active mixer at display-frame frequency. */
  advance(dt: number): void {
    this.updateMixer(dt);
  }

  captureContinuity(): EnemyAnimationContinuity {
    const action = this.instance.currentAction;
    const duration = action?.getClip().duration ?? 0;
    return {
      role: this.instance.currentRole,
      normalizedTime: duration > 0 ? THREE.MathUtils.euclideanModulo(action!.time / duration, 1) : this.instance.phaseSeed,
      dead: this.instance.dead,
      lastCueSequence: this.lastCueSequence,
    };
  }

  restoreContinuity(value: EnemyAnimationContinuity | null | undefined): void {
    if (!value?.role) return;
    this.instance.dead = false;
    this.playRole(value.role, { force: true, duration: 0 });
    const action = this.instance.currentAction;
    if (action) action.time = THREE.MathUtils.clamp(value.normalizedTime, 0, 1) * action.getClip().duration;
    this.instance.dead = value.dead;
    this.lastCueSequence = value.lastCueSequence;
    this.updateMixer(0);
  }

  private playRole(
    role: EnemyAnimationRole,
    opts: { force?: boolean; duration: number },
  ): void {
    const anim = this.instance;
    let targetRole = role;
    let action = anim.actions.get(role) ?? null;
    // Missing optional clips fall back through the profile chain.
    if (!action) {
      const resolved = this.resolver.resolve(this.profile, role, this.model.source.animations);
      if (resolved) {
        targetRole = resolved.role;
        action = anim.actions.get(resolved.role) ?? null;
      }
    }
    if (!action) return; // static pose fallback (no usable clip)
    const priority = this.stateMap[targetRole]?.interruptPriority ?? 0;
    if (!opts.force && anim.currentAction && priority < anim.currentPriority) return;
    const previousAction = anim.currentAction;
    anim.currentRole = targetRole;
    anim.currentAction = action;
    anim.currentPriority = priority;
    action.reset();
    action.enabled = true;
    action.paused = false;
    action.setEffectiveTimeScale(1);
    action.setEffectiveWeight(1);
    if (this.stateMap[targetRole]?.loop !== 'once') {
      action.setLoop(THREE.LoopRepeat, Infinity);
    }
    if (previousAction && previousAction !== action) {
      previousAction.fadeOut(opts.duration);
    }
    action.fadeIn(opts.duration);
    action.play();
  }

  private transitionDuration(current: EnemyAnimationRole | null, next: EnemyAnimationRole): number {
    const t = this.profile.transitions;
    if (next === 'death') return t.deathCrossFadeSeconds;
    if (isAttackRole(next)) return t.attackCrossFadeSeconds;
    if (next === 'hit' || next === 'stagger' || next === 'knockback') return t.hitCrossFadeSeconds;
    if (next === 'walk' || next === 'run' || next === 'hoverMove' || next === 'fastHover') {
      return t.locomotionCrossFadeSeconds;
    }
    if (current && (current === 'walk' || current === 'run')) return t.locomotionCrossFadeSeconds;
    return t.defaultCrossFadeSeconds;
  }

  private applyLocomotion(role: EnemyAnimationRole, speed: number): void {
    const action = this.instance.actions.get(role);
    if (!action) return;
    if (role !== 'walk' && role !== 'run' && role !== 'hoverMove' && role !== 'fastHover') return;
    const loc = this.profile.locomotion;
    const reference = role === 'run' || role === 'fastHover' ? loc.runSpeedReference : loc.walkSpeedReference;
    const scale = THREE.MathUtils.clamp(Math.abs(speed) / Math.max(0.001, reference), loc.playbackMin, loc.playbackMax);
    action.timeScale = scale;
    if (loc.randomStartPhase && action.time === 0 && this.instance.currentRole === role) {
      action.time = this.instance.phaseSeed * action.getClip().duration;
    }
  }

  private isFinishedOneShot(action: THREE.AnimationAction): boolean {
    if (action.loop !== THREE.LoopOnce) return false;
    return action.time >= action.getClip().duration - 0.001;
  }

  private updateMixer(dt: number): void {
    this.instance.lastUpdateTime += dt;
    this.instance.mixer.update(dt);
  }

  /** Manual mixer update with real elapsed time (mid-tier reduced rate). */
  updateMixerWithDelta(deltaSeconds: number): void {
    this.instance.lastUpdateTime += deltaSeconds;
    this.instance.mixer.update(deltaSeconds);
  }

  resolvedClipNames(): string[] {
    return this.resolver.resolvedNames(this.profile);
  }

  /** Preview/debug API: play a semantic role directly with default fade. */
  previewRole(role: EnemyAnimationRole): void {
    const anim = this.instance;
    if (role === 'death') {
      this.playRole(role, { force: true, duration: this.profile.transitions.deathCrossFadeSeconds });
      anim.dead = true;
      return;
    }
    anim.dead = false;
    this.playRole(role, { force: true, duration: this.transitionDuration(anim.currentRole, role) });
    this.applyLocomotion(role, 0);
    this.updateMixer(0);
  }

  dispose(): void {
    disposeAnimationInstance(this.instance);
  }
}
